import React, { useState } from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AuthTextField, PasswordTextField } from "./TextField";
import { useAuth } from "../../context/AuthContext";

const resetPasswordSubmitSchema = z
    .object({
        code: z.string().min(1, "Verification code is required"),
        newPassword: z
            .string()
            .min(8, "Password must be at least 8 characters")
            .regex(
                /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])/,
                "Password must include at least one uppercase letter, one lowercase letter, and one number"
            ),
        confirmPassword: z.string()
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: "Passwords do not match",
        path: ["confirmPassword"]
    });

type ResetPasswordSubmitForm = z.infer<typeof resetPasswordSubmitSchema>;

interface ForgotPasswordSubmitFormProps {
    email: string;
    onSubmit: (data: { code: string; newPassword: string }) => Promise<void>;
    onBack: () => void;
}

export default function ForgotPasswordSubmitForm({ email, onSubmit, onBack }: ForgotPasswordSubmitFormProps) {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { isLoading } = useAuth();

    const methods = useForm<ResetPasswordSubmitForm>({
        resolver: zodResolver(resetPasswordSubmitSchema),
        mode: "onChange"
    });

    const handleSubmit = methods.handleSubmit(async (data) => {
        await onSubmit({
            code: data.code,
            newPassword: data.newPassword
        });
    });

    return (
        <FormProvider {...methods}>
            <Box component="form" onSubmit={handleSubmit}>
                <Typography
                    variant="h4"
                    textAlign="center"
                    gutterBottom
                    color="primary"
                    sx={{ fontWeight: 500, mb: 3 }}
                >
                    Reset Password
                </Typography>

                <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mb: 3 }}>
                    Enter the verification code sent to {email} and your new password.
                </Typography>

                <AuthTextField
                    name="code"
                    label="Verification Code"
                    placeholder="Enter code from email"
                    fullWidth
                    margin="normal"
                />

                <PasswordTextField
                    name="newPassword"
                    label="New Password"
                    placeholder="Enter new password"
                    fullWidth
                    margin="normal"
                    showPassword={showPassword}
                    onTogglePasswordVisibility={() => setShowPassword(!showPassword)}
                />

                <PasswordTextField
                    name="confirmPassword"
                    label="Confirm Password"
                    placeholder="Confirm new password"
                    fullWidth
                    margin="normal"
                    showPassword={showConfirmPassword}
                    onTogglePasswordVisibility={() => setShowConfirmPassword(!showConfirmPassword)}
                />

                <Button
                    variant="contained"
                    type="submit"
                    fullWidth
                    sx={{
                        mt: 3,
                        py: 1.5,
                        fontSize: "1rem"
                    }}
                    disabled={!methods.formState.isValid}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
                </Button>

                <Button
                    variant="text"
                    type="button"
                    fullWidth
                    onClick={onBack}
                    sx={{
                        mt: 2,
                        color: "text.secondary"
                    }}
                >
                    Back to Login
                </Button>
            </Box>
        </FormProvider>
    );
}
